import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type { DSSetup, Service } from 'webapp-types';
import { equal } from 'webapp/utils/common';

import didUpdate from 'webapp/modifiers/did-update';
import FormRangeNullableComponent from 'webapp/components/form/range/nullable';

import BoardState from 'webapp/models-custom/board-state';
import DateRange from 'webapp/models-custom/date-range';
import MetricLimitations from 'webapp/models-custom/metric-limitations';
import Report from 'webapp/models-custom/report';
import type ReportSlide from 'webapp/models-custom/report-slide';
import type ReportSlideFragment from 'webapp/models-custom/report-slide-fragment';

import { reportDataboardsMDRChangedEventName } from 'webapp/services/event-manager';

interface Args {
	model: ReportSlide | Report;
	size?: string;
	theme?: string;
	customLabel?: string;
}

export default class ReportsMasterDateRangeSwitchComponent extends Component<Args> {
	@service('event-manager') declare eventManagerService: Service.EventManager;

	@tracked limitations: MetricLimitations | null = null;
	@tracked selectedMasterDateRange: DateRange | null = null;

	constructor(owner: Owner, args: Args) {
		super(owner, args);
		this.checkIfDateRangesAreCommon(true);

		if (this.isReportModel) {
			const report = this.args.model as Report;
			void report.fetchBoardPreviews();
		} else {
			const slide = this.args.model as ReportSlide;
			void slide.report?.fetchBoardPreviews();
		}
	}

	get hasSelection(): boolean {
		return !!this.selectedMasterDateRange;
	}

	get isReportModel(): boolean {
		return this.args.model instanceof Report;
	}

	get fragments(): ReportSlideFragment[] {
		return this.args.model.fragments;
	}

	@cached
	get metricData(): DSSetup.MetricData[] {
		return this.fragments.slice().reduce((metricData: DSSetup.MetricData[], fragment) => {
			const metricDataArray = fragment.block?.metricDataArray;

			if (metricDataArray) {
				metricDataArray.forEach((md) => {
					if (md && !metricData.find((md2) => equal(md, md2))) {
						metricData.push(md);
					}
				});
			}

			return metricData;
		}, []);
	}

	private createDateRange() {
		let dateRange;
		const selectedMasterDateRange = this.selectedMasterDateRange;
		if (this.hasSelection && selectedMasterDateRange) {
			dateRange = selectedMasterDateRange.createCopy();
		} else {
			dateRange = new DateRange();
		}
		return dateRange;
	}

	private resetLimitations() {
		const limitations = new MetricLimitations({
			metrics: this.metricData,
			dateRange: this.createDateRange(),
		});

		this.limitations = limitations;
	}

	private checkIfDateRangesAreCommon(isInitialCheck = false) {
		const fragments = this.fragments;
		if (fragments.length === 0) {
			return;
		}

		let firstDateRange: DateRange | null = null,
			areDateRangesTheSame = true;

		const fragmentsWithMetricData = fragments.filter(
			(fragment) => fragment.isFragmentWithMetricData,
		);
		for (const fragment of fragmentsWithMetricData) {
			const fragmentDateRangeInterface = fragment.block?.dateRange;

			if (fragmentDateRangeInterface) {
				if (!firstDateRange) {
					firstDateRange = DateRange.createFromFormula(fragmentDateRangeInterface);
				} else if (!firstDateRange.isRangeEqual(fragmentDateRangeInterface)) {
					areDateRangesTheSame = false;
					break;
				}
			}
		}

		if (this.isReportModel && areDateRangesTheSame) {
			const report = this.args.model as Report;
			const boardFragments = report.boardFragments;
			for (const fragment of boardFragments) {
				const board = fragment.boardOrPreview;
				if (board) {
					const boardState = BoardState.createFromBoard(board);
					const boardDateRangeInterface = boardState.selectedMasterDateRange?.formula;
					if (boardDateRangeInterface) {
						if (!firstDateRange) {
							firstDateRange = DateRange.createFromFormula(boardDateRangeInterface);
						} else if (!firstDateRange.isRangeEqual(boardDateRangeInterface)) {
							areDateRangesTheSame = false;
							break;
						}
					} else {
						areDateRangesTheSame = false;
						break;
					}
				}
			}
		}

		if (areDateRangesTheSame && firstDateRange) {
			this.selectedMasterDateRange = firstDateRange;
			if (!isInitialCheck && this.limitations) {
				this.limitations.updateDateRangeFromFormula(firstDateRange.formula);
			} else {
				this.resetLimitations();
			}
		} else {
			this.selectedMasterDateRange = null;
			this.resetLimitations();
		}
	}

	private updateLimitationsFromMetricData() {
		// next, to trigger only after all blocks have been added to the boardState
		next(() => {
			this.limitations?.changeMetrics(this.metricData);
		});
	}

	@action
	onMetrickeysChange() {
		this.updateLimitationsFromMetricData();
		this.checkIfDateRangesAreCommon();
	}

	@action
	updateDateRange(dateRange: DateRange | null) {
		if (dateRange && !this.selectedMasterDateRange?.isEqual(dateRange.formula)) {
			this.selectedMasterDateRange = dateRange;

			if (this.isReportModel) {
				const report = this.args.model as Report,
					boardFragments = report.boardFragments,
					unconsumedBoardFragments: ReportSlideFragment[] = [];

				// use slide updateDateRange logic for metric data fragments
				report.slides
					.filter((slide) => slide.hasFragmentsWithMetricData)
					.forEach((slide) => slide.updateDateRange(dateRange));

				// trigger event for each board fragment individually
				// track unconsumed fragments that did not handle the event (not loaded / different slide when on slides view)
				boardFragments.forEach((fragment) => {
					const wasEventConsumed = this.eventManagerService.trigger(
						reportDataboardsMDRChangedEventName(fragment.id),
						{ dateRange },
					);

					if (!wasEventConsumed) {
						unconsumedBoardFragments.push(fragment);
					}
				});
				// for unconsumed board fragments, change date range manually with boardState.updateMasterDateRangeSettings
				unconsumedBoardFragments.forEach((fragment) => {
					const board = fragment.boardOrPreview;
					if (board) {
						const boardState = BoardState.createFromBoard(board);
						boardState.isReportEditor = true;
						boardState.updateMasterDateRangeSettings(dateRange, null);
						boardState.onPreviewHashChange = (previewHash) => {
							fragment.updatePreviewHash(previewHash);
							void fragment.saveAndCatch();
						};
					}
				});
			} else {
				const slide = this.args.model as ReportSlide;
				slide.updateDateRange(dateRange);
			}
		}

		if (!dateRange && !this.limitations?.dateRange?.isEmpty) {
			this.resetLimitations(); // bcos we want clean slate when range picker is opened
		}
	}
	<template>
		<div class="master-date-range" {{didUpdate this.onMetrickeysChange this.metricData}}>
			{{#if this.limitations}}
				<FormRangeNullableComponent
					@metricLimitations={{this.limitations}}
					@hasSelection={{this.hasSelection}}
					@onChange={{this.updateDateRange}}
					@theme={{@theme}}
					@size={{@size}}
					@customLabel={{@customLabel}}
				/>
			{{/if}}

		</div>
	</template>
}
